import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { usePagedData } from '../../hooks/usePagedData';
import { productsApi } from '../../api/endpoints';
import { ProductList, ProductStatusMap, ChannelMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSearch, FiDownload, FiX, FiDollarSign } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';
import { SkeletonTable } from '../../components/common/Skeleton';

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [brandInput, setBrandInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<ProductList>(productsApi.list, {
    initialSortBy: 'title',
    initialSortDesc: false,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleChannelFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        channel: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleBrandFilter = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        brand: brandInput || undefined,
      }));
    },
    [brandInput, setFilters]
  );

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await productsApi.sync();
      toast.success('Synchronizacja produktow rozpoczeta');
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad synchronizacji');
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const handleBulkSyncPricing = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    try {
      const result = await productsApi.bulkSyncPricing(Array.from(selectedIds));
      const r = result.data;
      toast.success(`Zsynchronizowano ceny ${r.success} z ${r.total} produktów`);
      setSelectedIds(new Set());
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd bulk sync cen');
    } finally {
      setBulkProcessing(false);
    }
  }, [selectedIds, refresh]);

  const handleRowClick = useCallback(
    (row: ProductList) => {
      navigate(`/products/${row.id}`);
    },
    [navigate]
  );

  const formatCurrency = (amount: number | null) =>
    amount !== null
      ? amount.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' })
      : '-';

  const columns = [
    {
      key: 'asin',
      header: 'ASIN',
      sortable: true,
    },
    {
      key: 'sku',
      header: 'SKU',
      sortable: true,
      render: (row: ProductList) => row.sku || '-',
    },
    {
      key: 'title',
      header: 'Tytul',
      sortable: true,
      render: (row: ProductList) => (
        <span className="text-truncate" title={row.title}>
          {row.title.length > 60 ? row.title.substring(0, 60) + '...' : row.title}
        </span>
      ),
    },
    {
      key: 'brand',
      header: 'Marka',
      sortable: true,
      render: (row: ProductList) => row.brand || '-',
    },
    {
      key: 'listPrice',
      header: 'Cena',
      sortable: true,
      render: (row: ProductList) => formatCurrency(row.listPrice),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: ProductList) => (
        <StatusBadge type="product" value={row.status} />
      ),
    },
    {
      key: 'channel',
      header: 'Kanal',
      render: (row: ProductList) => (
        <StatusBadge type="channel" value={row.channel} />
      ),
    },
    {
      key: 'availableQuantity',
      header: 'Dostepne',
      sortable: true,
      render: (row: ProductList) => row.availableQuantity,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Produkty</h1>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.productsXlsx, `produkty_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? 'Synchronizacja...' : 'Synchronizuj z Amazon'}
          </button>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span style={{ fontWeight: 600 }}>Zaznaczono: {selectedIds.size}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary btn-sm" onClick={handleBulkSyncPricing} disabled={bulkProcessing}>
              <FiDollarSign /> {bulkProcessing ? 'Synchronizuję...' : 'Sync cen zaznaczonych'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds(new Set())} disabled={bulkProcessing}>
              <FiX /> Odznacz
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Szukaj produktow..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: 'Wszystkie statusy' },
              ...Object.entries(ProductStatusMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder="Wszystkie statusy"
          />

          <Select
            options={[
              { value: '', label: 'Wszystkie kanaly' },
              ...Object.entries(ChannelMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.channel ?? '')}
            onChange={handleChannelFilter}
            placeholder="Wszystkie kanaly"
          />

          <form onSubmit={handleBrandFilter} style={{ display: 'flex', gap: '0.25rem' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Filtruj marke..."
              value={brandInput}
              onChange={(e) => setBrandInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary btn-sm">
              <FiSearch />
            </button>
          </form>
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={10} cols={8} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}
    </div>
  );
};

export default ProductsPage;
